import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { CtrlIconComponent } from '@co-3/ctrl-ui/icons';
import { DamageReportFileAttachment } from '@models/damage-report';
import { DamageDocumentTypePipe } from '../../pipes';
import { AsyncPipe } from '@angular/common';
import { splitFileName } from 'app/utils/helper.functions';
import { DamageReportsService } from '../../damage-reports.service';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Observable, filter, from, map, switchMap, throwError } from 'rxjs';

@Component({
    selector: 'app-attached-file-list-item',
    imports: [CtrlIconComponent, DamageDocumentTypePipe, AsyncPipe],
    templateUrl: './attached-file-list-item.component.html',
    styleUrl: './attached-file-list-item.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttachedFileListItemComponent {
    private damageReportService = inject(DamageReportsService);
    private sanitizer = inject(DomSanitizer);

    public fileAttachment = input.required<DamageReportFileAttachment>();
    public insuranceTypeId = input.required<number>();

    protected fileName = computed(() => this.fileAttachment().url);
    protected splittedFileName = computed(() => splitFileName(this.fileName()));

    protected isImage = computed(() => /(.*)(.jp[e]?g|.png)$/i.test(this.fileName()));

    protected icon = computed(() => {
        const fileName = this.fileName();
        if (this.isPdf(fileName)) {
            return 'file_pdf';
        } else {
            return 'file';
        }
    });

    protected downloadUrl = signal<SafeUrl | null>(null);

    public constructor() {
        effect(() => {
            const attachment = this.fileAttachment();
            if (attachment) {
                this.getAttachmentBlob(attachment).subscribe((blob) => this.downloadUrl.set(blob.imageData));
            }
        });
    }

    private isPdf(fileName: string): boolean {
        return fileName.endsWith('.pdf');
    }

    private getAttachmentBlob(document: DamageReportFileAttachment): Observable<{ blob: Blob; imageData: SafeUrl }> {
        return this.damageReportService.getDamageDocumentBlob(document).pipe(
            switchMap((blob) => this.convertToTypedBlob(blob)),
            filter(
                (blob: Blob) =>
                    blob.type === 'image/jpeg' || blob.type === 'image/png' || blob.type === 'application/pdf',
            ),
            map((blob) => {
                const urlCreator = window.URL;
                const imageData = this.sanitizer.bypassSecurityTrustUrl(urlCreator.createObjectURL(blob));

                return {
                    blob,
                    imageData,
                };
            }),
        );
    }

    // Returns a new Blob with corrected MIME type
    private convertToTypedBlob(blob: Blob): Observable<Blob> {
        return this.detectFileType(blob).pipe(
            map((type) => {
                if (!type) {
                    throwError(() => new Error('Blob does not contain a recognized format'));
                }
                return new Blob([blob], { type });
            }),
        );
    }

    private detectFileType(blob: Blob): Observable<string> {
        return from(blob.slice(0, 16).arrayBuffer()).pipe(
            map((buffer) => {
                const header = new Uint8Array(buffer);

                // --- PNG ---
                const pngSig = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
                if (pngSig.every((b, i) => header[i] === b)) {
                    return 'image/png';
                }

                // --- JPEG ---
                if (header[0] === 0xff && header[1] === 0xd8) {
                    return 'image/jpeg';
                }

                // --- PDF ---
                const ascii5 = String.fromCharCode(...header.slice(0, 5));
                if (ascii5 === '%PDF-') {
                    return 'application/pdf';
                }

                return null;
            }),
        );
    }
}
